import { Dispatch } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import type { GerenciadorPrecos } from '../../../../../@types/GerenciadorPrecos';
import { RootState } from '../../../../../@types/RootState';
import { queryClient } from '../../../../../App';
import { AlertWithTimestamp } from '../../../../../components/AlertWithTimestamp';
import { selectorSaveFilters, SET_IS_OPEN, SET_NAME } from '../../../../../reducers/ipa/priceManager/modals/saveFilters';
import { selectorFiltersData } from '../../../../../reducers/gerenciadorPrecos/filters/filtersData';
import { selectorFiltersValues } from '../../../../../reducers/gerenciadorPrecos/filters/filtersValues';
import { usePriceManagerDatapointsModel } from '../../hooks/usePriceManagerDatapointsModel';
import { postSavedFilter, PostSavedFitersDataProps, putSavedFilter } from '../../services_ts';
import { SalvarFiltrosModal } from '../SalvarFiltrosModal';
import { toSavedResponsibleValues } from '../SavedFilters/utils';
import type { ResponsibleFilterOption } from '../../../../Administrador/Produtos/Components/SetupIpaPricingResponsibilities/service';
import { stripInjectedResponsibleCategories } from '../../../../../components/NewFilterBox/data/responsibleFilter';

type SaveFiltersModalWrapperProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;
type SavedPriceManagerFilters = ReturnType<typeof usePriceManagerDatapointsModel> & {
  responsible: GerenciadorPrecos.SavedResponsible[];
};

const SaveFiltersModalWrapper = ({ saveFilters: { isOpen, data, name }, closeModal, setName }: SaveFiltersModalWrapperProps) => {
  const filters = usePriceManagerDatapointsModel();
  const filtersValues = useSelector(selectorFiltersValues);
  const filtersData = useSelector(selectorFiltersData);

  const savedFilters = useMemo<SavedPriceManagerFilters>(() => {
    const responsibleOption = (filtersData.responsible ?? []).find(item => item.value === filtersValues.responsible?.[0]) as
      | ResponsibleFilterOption
      | undefined;
    const filtersWithoutProjection = stripInjectedResponsibleCategories(
      filters as Record<string, unknown>,
      responsibleOption?.categoryLevel ?? null,
      responsibleOption?.injectedCategories ?? [],
    );

    return {
      ...filtersWithoutProjection,
      responsible: toSavedResponsibleValues(filtersValues.responsible, filtersData.responsible as ResponsibleFilterOption[]),
    } as SavedPriceManagerFilters;
  }, [filters, filtersData.responsible, filtersValues.responsible]);

  const model: PostSavedFitersDataProps<SavedPriceManagerFilters> = useMemo(
    () => ({
      id: data?.value ?? '',
      name: name ?? '',
      screen: 'PRICE_MANAGER',
      filters: savedFilters,
    }),
    [data, name, savedFilters],
  );

  const actions = useMemo(() => {
    const refetchSaveFilters = async () => {
      return await queryClient.invalidateQueries(['ipa.filter.savedFilters']);
    };

    const saveFilters = async () => {
      try {
        const response = await postSavedFilter(model);
        if (response.status !== 200) return;
        await refetchSaveFilters();
        closeModal();
      } catch (err) {
        const message = err.response.data._embedded.errors[0].message;
        AlertWithTimestamp({ message });
      }
    };

    const editFilters = async () => {
      try {
        const response = await putSavedFilter(model);
        if (response.status !== 201) return;
        await refetchSaveFilters();
        closeModal();
      } catch (err) {
        const message = err.response.data._embedded.errors[0].message;
        AlertWithTimestamp({ message });
      }
    };

    return { POST: saveFilters, PUT: editFilters };
  }, [closeModal, model]);

  return <SalvarFiltrosModal show={isOpen} onHide={closeModal} actions={actions} onChange={setName} savedFilter={{ data, name }} />;
};

const mapStateToProps = (state: RootState) => ({
  saveFilters: selectorSaveFilters(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeModal: () => dispatch(SET_IS_OPEN(false)),
  setName: (name: string) => dispatch(SET_NAME(name)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SaveFiltersModalWrapper);
